import Button from "@core/ui/Button";
import cn from "classnames";
import Image from "next/image";
import { Fragment, ReactNode, useEffect, useState } from "react";

import { NavigationLink } from "./types";

interface MobileMenuProps {
  callToAction: ReactNode;
  ctaButtonLabel: string;
  isOpen: boolean;
  navigationLinks: NavigationLink[];
  onClickCTA: () => void;
  onToggleMenu: () => void;
}

export default function MobileMenu({
  callToAction,
  ctaButtonLabel,
  isOpen,
  navigationLinks,
  onClickCTA,
  onToggleMenu,
}: MobileMenuProps): JSX.Element {
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.style.overflow = isOpen ? "hidden" : "scroll";
    }
  }, [isOpen]);

  const [openSubmenuId, setOpenSubmenuId] = useState<string | null>(null);

  return (
    <>
      <header className="mx-auto flex h-[58px] max-w-[1072px] items-center justify-between space-x-12 py-4 px-6 laptop:hidden">
        <button
          type="button"
          className="h-[26px] w-[88px]"
          onClick={() => {
            window.location.href = process.env.NEXT_PUBLIC_WEBSITE_BASE_URL;
            return undefined;
          }}
        >
          <Image
            src="/images/nocd-logo.svg"
            alt="Go to homepage"
            width={120}
            height={35}
            priority
          />
        </button>

        <nav className="flex items-center space-x-4">
          {callToAction}
          <button
            type="button"
            onClick={onToggleMenu}
            aria-label={isOpen ? "Open menu" : "Close menu"}
          >
            <div
              className={cn(
                `mb-2 h-[2px] w-8 rounded bg-gray-900 transition-transform duration-300`,
                isOpen && "translate-y-3 rotate-45 transform"
              )}
            />
            <div
              className={cn(
                `mb-2 h-[2px] w-8 rounded bg-gray-900 transition-transform duration-300`,
                isOpen ? "opacity-0" : "opacity-100"
              )}
            />
            <div
              className={cn(
                `h-[2px] w-8 rounded bg-gray-900 transition-transform duration-300`,
                isOpen && "-translate-y-2 -rotate-45 transform"
              )}
            />
          </button>
        </nav>
      </header>

      <nav
        className={cn(
          "fixed overflow-y-scroll left-0 right-0 flex top-[58px] bottom-0 w-full transform bg-[#F0F1FF] transition-transform laptop:hidden",
          isOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        <div className="mx-auto flex max-w-[1072px] flex-1 flex-col px-6 pt-3">
          <ul className="text-16px">
            {navigationLinks
              .filter(
                ({ hide, id }) =>
                  !hide && (openSubmenuId === null || openSubmenuId === id)
              )
              .map(({ Component, title, id, subMenu }) => (
                <Fragment key={id}>
                  {openSubmenuId === id ? null : (
                    <li>
                      <Component
                        onClick={() => {
                          setOpenSubmenuId(id);
                        }}
                        className="flex items-center py-3"
                      >
                        {title}
                      </Component>
                    </li>
                  )}

                  {openSubmenuId === id ? (
                    <li className="mb-4 mt-2">
                      <button
                        type="button"
                        onClick={() => setOpenSubmenuId(null)}
                        className="text-indigo-600 flex items-center space-x-2"
                      >
                        <span>
                          <svg
                            width="13"
                            height="10"
                            viewBox="0 0 13 10"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M11.55 4.675L0.549987 4.675M0.549987 4.675L4.67499 8.8M0.549987 4.675L4.67499 0.55"
                              stroke="#545CEA"
                              stroke-width="1.1"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                        </span>
                        <span className="font-bold">{title}</span>
                      </button>

                      <div className="mt-4">{subMenu}</div>
                    </li>
                  ) : null}
                </Fragment>
              ))}
          </ul>

          <hr className="border-t-2 border-[#E3E4FC] my-5" />

          <div className="flex flex-col justify-center space-y-4 pb-6 max-w-70 mx-auto">
            <span className="text-center text-14px">
              The fastest way to connect with us is by booking a{" "}
              <strong>free 15-minute call</strong>. For other questions,{" "}
              <a
                href="mailto:care@nocdhelp.com"
                className="text-center text-teal-600 underline"
              >
                contact us
              </a>
              .
            </span>

            <Button
              className="mx-auto h-13 !rounded-lg !font-semibold"
              color="indigo"
              onClick={onClickCTA}
            >
              {ctaButtonLabel}
            </Button>
          </div>
        </div>
      </nav>
    </>
  );
}
